import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';

const Services = () => {
  const { t } = useLanguage();

  const services = [
    {
      id: 'capital',
      number: '01',
      title: t('services.capital.title'),
      description: t('services.capital.description'),
      tiers: [
        { name: t('services.capital.tier1Name'), description: t('services.capital.tier1Desc') },
        { name: t('services.capital.tier2Name'), description: t('services.capital.tier2Desc') },
        { name: t('services.capital.tier3Name'), description: t('services.capital.tier3Desc') }
      ]
    },
    {
      id: 'revenue',
      number: '02',
      title: t('services.revenue.title'),
      description: t('services.revenue.description'),
      tiers: [
        { name: t('services.revenue.tier1Name'), description: t('services.revenue.tier1Desc') },
        { name: t('services.revenue.tier2Name'), description: t('services.revenue.tier2Desc') },
        { name: t('services.revenue.tier3Name'), description: t('services.revenue.tier3Desc') }
      ]
    },
    {
      id: 'ibv',
      number: '03',
      title: t('services.ibv.title'),
      description: t('services.ibv.description'),
      tiers: [
        { name: t('services.ibv.tier1Name'), description: t('services.ibv.tier1Desc') },
        { name: t('services.ibv.tier2Name'), description: t('services.ibv.tier2Desc') },
        { name: t('services.ibv.tier3Name'), description: t('services.ibv.tier3Desc') }
      ]
    },
  ];

  return (
    <section 
      id="services"
      data-testid="services-section"
      className="py-20 md:py-28 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#60B5AF]/10 mb-6"
          >
            <span className="text-sm font-body text-[#1A3A4A] font-medium tracking-wide uppercase">{t('services.badge')}</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            data-testid="services-title"
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A3A4A] tracking-tight mb-6"
          >
            {t('services.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="font-body text-lg text-[#1A3A4A]/60 max-w-2xl mx-auto"
          >
            {t('services.subtitle')}
          </motion.p>
        </div>

        {/* Timeline Layout - Large numbered steps */}
        <div className="space-y-0">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              data-testid={`service-card-${service.id}`}
              className={`relative ${index !== services.length - 1 ? 'pb-12 md:pb-16' : ''}`}
            >
              {/* Vertical connecting line */}
              {index !== services.length - 1 && (
                <div className="absolute left-6 md:left-12 top-20 bottom-0 w-px bg-gradient-to-b from-[#60B5AF]/30 to-transparent" />
              )}

              <div className="flex gap-6 md:gap-12">
                {/* Large Number - Timeline marker */}
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 md:w-24 md:h-24 rounded-md bg-[#1A3A4A] flex items-center justify-center">
                    <span className="font-heading text-xl md:text-4xl font-bold text-white">
                      {service.number}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-grow pt-1">
                  <h3 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-[#1A3A4A] mb-3">
                    {service.title}
                  </h3>
                  <p className="font-body text-[#1A3A4A]/70 mb-6 max-w-2xl leading-relaxed">
                    {service.description}
                  </p>

                  {/* Sub-items in horizontal grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {service.tiers.map((tier, tierIndex) => (
                      <div 
                        key={tierIndex} 
                        className="bg-white rounded-md p-5 border border-[#E5E7EB] hover:border-[#60B5AF]/40 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#60B5AF] mt-2 flex-shrink-0" />
                          <div>
                            <p className="font-body font-semibold text-[#1A3A4A] text-sm mb-1">
                              {tier.name}
                            </p>
                            <p className="font-body text-[#1A3A4A]/50 text-sm leading-relaxed">
                              {tier.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
